import { AlertVM } from '../../types/alerts'
import { CheckCircle2, ChevronRight, RefreshCw, Sparkles, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import MainPane from '../MainPane'

interface Props {
  alert?: AlertVM | null
  onSave: (id: string) => void
  onClose: () => void
}

const badgeColor = (color: 'red' | 'orange' | 'green' | 'blue' | 'gray'): string => {
  switch (color) {
    case 'red':
      return 'bg-rose-100 text-rose-700 border-rose-200'
    case 'orange':
      return 'bg-orange-100 text-orange-700 border-orange-200'
    case 'green':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200'
    case 'blue':
      return 'bg-blue-100 text-blue-700 border-blue-200'
    case 'gray':
      return 'bg-slate-100 text-slate-700 border-slate-200'
    default:
      return 'bg-white/80 text-slate-700 border-white/40'
  }
}

function priorityClasses(priority?: string): string {
  switch (priority) {
    case 'Urgent':
      return 'bg-rose-600 text-white'
    case 'High':
      return 'bg-orange-500 text-white'
    case 'Medium':
      return 'bg-amber-400 text-white'
    case 'Low':
      return 'bg-emerald-400 text-white'
    default:
      return 'bg-slate-300 text-white'
  }
}

function matchTypeClasses(type?: string): string {
  // Match type badges on dark gradient background - use white/light backgrounds with colored text
  switch (type) {
    case 'Needs Match':
      return 'bg-white/90 text-violet-700 border-violet-300'
    case 'Ideal Profile':
      return 'bg-white/90 text-fuchsia-700 border-fuchsia-300'
    case 'External':
      return 'bg-white/90 text-cyan-700 border-cyan-300'
    default:
      return 'bg-white/90 text-slate-700 border-slate-300'
  }
}

function statusClasses(status?: string): string {
  // Status badge on dark gradient background - use white/light backgrounds with colored text
  switch (status) {
    case 'New':
      return 'bg-white/90 text-blue-700 border-blue-300'
    case 'Opened':
      return 'bg-white/90 text-indigo-700 border-indigo-300'
    case 'Actioned':
      return 'bg-white/90 text-green-700 border-green-300'
    case 'Dismissed':
      return 'bg-white/70 text-gray-700 border-gray-300'
    default:
      return 'bg-white/90 text-slate-700 border-slate-300'
  }
}

function formatCreatedDate(ts?: string): string {
  if (!ts) return '—'
  const d = new Date(ts)
  const diff = Date.now() - d.getTime()
  const min = 60 * 1000
  const hr = 60 * min
  const day = 24 * hr
  const week = 7 * day
  
  // Relative time for < 7 days
  if (diff < week) {
    if (diff < hr) {
      return `${Math.max(1, Math.floor(diff / min))} min ago`
    }
    if (diff < day) {
      const hours = Math.floor(diff / hr)
      return `${hours} hour${hours > 1 ? 's' : ''} ago`
    }
    const days = Math.floor(diff / day)
    return `${days} day${days > 1 ? 's' : ''} ago`
  }
  
  // Absolute date for ≥ 7 days
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AlertDetailPanel({ alert, onSave, onClose }: Props) {
  if (!alert) {
    return <div className="h-full flex items-center justify-center text-gray-400">Select an alert to view details</div>
  }

  const [showFeatureDrawer, setShowFeatureDrawer] = useState<boolean>(false)
  const sections = alert.sections ?? []
  const hasStructuredSections = sections.length > 0
  const featureSection = sections.find(section => section.id === 'feature-fit')
  const badges = alert.badges ?? []

  const [openSections, setOpenSections] = useState<Record<string, boolean>>({})

  useEffect(() => {
    const initial: Record<string, boolean> = {}
    sections.forEach(section => {
      if (section.collapsible) {
        initial[section.id] = section.defaultOpen ?? false
      } else {
        initial[section.id] = true
      }
    })
    setOpenSections(initial)
  }, [alert.id, sections])

  const toggleSection = (id: string) => {
    setOpenSections(prev => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const toneClasses = (tone?: 'positive' | 'negative' | 'neutral'): string => {
    switch (tone) {
      case 'positive':
        return 'text-emerald-700'
      case 'negative':
        return 'text-rose-700'
      case 'neutral':
        return 'text-slate-700'
      default:
        return 'text-slate-700'
    }
  }

const chipToneClasses = (tone?: 'primary' | 'secondary' | 'positive' | 'negative' | 'neutral'): string => {
  switch (tone) {
    case 'positive':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200'
    case 'negative':
      return 'bg-rose-50 text-rose-700 border-rose-200'
    case 'neutral':
      return 'bg-gray-100 text-gray-700 border-gray-200'
    case 'secondary':
      return 'bg-slate-100 text-slate-700 border-slate-200'
    case 'primary':
    default:
      return 'bg-indigo-50 text-indigo-700 border-indigo-200'
  }
}

const highlightToneClasses = (tone?: 'positive' | 'negative' | 'neutral'): string => {
  switch (tone) {
    case 'positive':
      return 'bg-emerald-50 border-emerald-200'
    case 'negative':
      return 'bg-rose-50 border-rose-200'
    case 'neutral':
    default:
      return 'bg-indigo-50 border-indigo-200'
  }
}

  const renderItems = (items: NonNullable<AlertVM['sections']>[number]['items']) => {
    if (!items?.length) return null
    return (
      <dl className="grid gap-4 sm:grid-cols-2">
        {items.map((item, idx) => (
        <div key={`${item.label}-${idx}`} className="space-y-1">
          <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{item.label}</dt>
          <dd className="text-sm leading-5 flex items-center gap-2">
            <span className={toneClasses(item.tone)}>{item.value}</span>
            {item.trendChip && (
              <span className={`text-[10px] px-1.5 py-0.5 rounded-full border ${chipToneClasses(item.trendChip.tone)}`}>
                {item.trendChip.label}
              </span>
            )}
          </dd>
          </div>
        ))}
      </dl>
    )
  }

  const renderBullets = (bullets: string[] | undefined, variant: 'default' | 'actions' = 'default') => {
    if (!bullets?.length) return null
    if (variant === 'actions') {
      return (
        <ul className="space-y-2 text-sm text-gray-700">
          {bullets.map((line, idx) => (
            <li key={idx} className="flex items-start gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )
    }

    return (
      <ul className="list-disc pl-5 space-y-2 text-sm text-gray-700">
        {bullets.map((line, idx) => (
          <li key={idx}>{line}</li>
        ))}
      </ul>
    )
  }

  const renderEngagements = (engagements: Array<{ date: string; type: string; description: string; url?: string }> | undefined) => {
    if (!engagements?.length) return null
    
    return (
      <div className="space-y-3">
        {engagements.map((eng, idx) => (
          <div key={idx} className="border-l-2 border-gray-200 pl-3 space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">
                {eng.type}
              </span>
              <span className="text-xs text-gray-600">{eng.date}</span>
              {eng.url && (
                <a
                  href={eng.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:text-indigo-800 p-1 rounded hover:bg-indigo-50 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                  aria-label="View source"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
            <p className="text-sm text-gray-700">{eng.description}</p>
          </div>
        ))}
      </div>
    )
  }

  const renderBody = (body?: string) => {
    if (!body) return null
    return body
      .split('\n')
      .map(line => line.trim())
      .filter(Boolean)
      .map((line, idx) => (
        <p key={idx} className="text-sm text-gray-700">
          {line}
        </p>
      ))
  }

  const renderSectionLink = (section: NonNullable<AlertVM['sections']>[number]) => {
    if (section.id === 'feature-fit') {
      return (
        <div className="flex items-center gap-2">
          {section.link && (
            <a
              className="text-xs text-indigo-700 hover:underline inline-flex items-center gap-1"
              href={section.link.href}
              target="_blank"
              rel="noreferrer"
            >
              <ExternalLink className="w-3.5 h-3.5" /> {section.link.label}
            </a>
          )}
          <button
            className="text-xs text-indigo-700 hover:underline inline-flex items-center gap-1"
            onClick={() => setShowFeatureDrawer(true)}
          >
            <Sparkles className="w-3.5 h-3.5" /> View full feature brief
          </button>
        </div>
      )
    }

    if (section.link) {
      return (
        <a
          className="text-xs text-indigo-700 hover:underline inline-flex items-center gap-1"
          href={section.link.href}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink className="w-3.5 h-3.5" /> {section.link.label}
        </a>
      )
    }

    return null
  }

  const structuredSections = useMemo(() => {
    if (!hasStructuredSections) return []
    return sections.map(section => ({
      ...section,
      isOpen: openSections[section.id] ?? true,
    }))
  }, [sections, hasStructuredSections, openSections])

  return (
    <MainPane
      className="w-full"
      headerClassName="bg-gradient-to-r from-indigo-600 via-blue-600 to-sky-600 text-white border-white/20 border-b-0"
      headerInnerClassName="px-5 py-3"
      title={(
        <div className="w-full max-w-none relative">
          {/* Row 1: Account Name, Feature Name, Match Type, Status, with timestamp on right bottom */}
          <div className="flex items-center justify-between gap-2 flex-wrap text-white mb-2">
            <div className="flex items-center gap-2 flex-wrap min-w-0 flex-1">
              <span className="text-xl font-semibold truncate">{alert.accountName}</span>
              {alert.featureName && (
                <>
                  <RefreshCw className="w-4 h-4 text-white/80 flex-shrink-0" />
                  <span className="text-xl font-semibold truncate">{alert.featureName}</span>
                </>
              )}
              {alert.matchType && (
                <span className={`text-xs px-2 py-0.5 rounded-full border ${matchTypeClasses(alert.matchType)}`}>{alert.matchType}</span>
              )}
            {alert.status && (
              <span className={`text-xs px-2 py-0.5 rounded-full border ${statusClasses(alert.status)}`}>{alert.status}</span>
            )}
          </div>
            <span className="text-xs text-white/80 ml-auto">{formatCreatedDate(alert.createdAt)}</span>
          </div>
          {/* Row 2: Priority plus key health cues */}
          <div className="flex flex-wrap items-center gap-2">
            {alert.priority && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${priorityClasses(alert.priority)}`}>{alert.priority}</span>
            )}
            {badges.map((badge, idx) => (
              <span
                key={`${badge.label}-${idx}`}
                className={`text-xs px-2 py-0.5 rounded-full border ${badge.color ? badgeColor(badge.color) : 'bg-white/80 text-slate-700 border-white/40'}`}
              >
                {badge.label}
              </span>
            ))}
          </div>
        </div>
      )}
      detailsOpen={showFeatureDrawer}
      detailsTitle={`About ${alert.featureName || 'this feature'}`}
      detailsPane={(
        <div className="text-sm text-gray-700 space-y-4">
          {featureSection ? (
            <>
              {renderItems(featureSection.items)}
              {renderBody(featureSection.body)}
              {renderBullets(featureSection.bullets)}
              {featureSection.link && (
                <a
                  className="text-sm text-indigo-700 hover:underline inline-flex items-center gap-1"
                  href={featureSection.link.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink className="w-4 h-4" /> {featureSection.link.label}
                </a>
              )}
            </>
          ) : (
            <p>No additional feature details provided.</p>
          )}
        </div>
      )}
      onCloseDetails={() => setShowFeatureDrawer(false)}
    >
      <div className="p-6">
        {hasStructuredSections ? (
          <div className="max-w-5xl mx-auto flex flex-col gap-6">
            {structuredSections.map(section => (
              <section key={section.id} className="border border-gray-200 rounded-xl bg-white shadow-sm">
                <div className="flex items-center justify-between gap-3 px-5 py-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900">{section.title}</h3>
                  </div>
                  <div className="flex items-center gap-3">
                    {renderSectionLink(section)}
                    {section.collapsible && (
                      <button
                        className="p-1.5 rounded-md border border-gray-200 text-gray-500 hover:bg-gray-50"
                        onClick={() => toggleSection(section.id)}
                        aria-label={section.isOpen ? 'Collapse section' : 'Expand section'}
                      >
                        {section.isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    )}
                  </div>
                </div>
                {section.isOpen && (
                  <div className="px-5 pb-5 space-y-4">
                    {section.chips?.length ? (
                      <div className="flex flex-wrap gap-2">
                        {section.chips.map((chip, idx) => (
                          <span
                            key={`${chip.label}-${idx}`}
                            className={`text-xs px-2 py-0.5 rounded-full border ${chipToneClasses(chip.tone)}`}
                          >
                            {chip.label}
                          </span>
                        ))}
                      </div>
                    ) : null}
                    {section.highlight ? (
                      <div className={`rounded-lg border px-3 py-3 ${highlightToneClasses(section.highlight.tone)} space-y-2`}>
                        <div className="flex flex-wrap items-center gap-2">
                          {section.highlight.label && (
                            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-600">
                              {section.highlight.label}
                            </div>
                          )}
                          {section.highlight.chips?.map((chip, idx) => (
                            <span
                              key={`highlight-chip-${idx}`}
                              className={`text-[11px] px-2 py-0.5 rounded-full border ${chipToneClasses(chip.tone)}`}
                            >
                              {chip.label}
                            </span>
                          ))}
                        </div>
                        <div className="text-sm font-medium text-gray-900">{section.highlight.text}</div>
                      </div>
                    ) : null}
                    {renderItems(section.items)}
                    {renderBody(section.body)}
                    {section.id === 'relevant-engagements' && section.engagements
                      ? renderEngagements(section.engagements)
                      : renderBullets(section.bullets, section.id === 'actions' ? 'actions' : 'default')}
                    {section.id === 'actions' && (
                      <div className="pt-2 flex flex-wrap gap-3">
                        <button className="px-3 py-2 text-sm bg-tandem-blue text-white rounded-lg hover:bg-blue-700 inline-flex items-center">
                          <ChevronRight className="w-4 h-4 mr-1" /> Take Action
                        </button>
                        <button className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50">Share with Team</button>
                      </div>
                    )}
          </div>
                )}
              </section>
            ))}
          </div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-6">
            <section className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Why this matters</h3>
          <p className="text-sm text-gray-700">
                {alert.summary || 'This match highlights a relevant product opportunity based on recent signals.'}
          </p>
        </section>
            <section className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Recommended next steps</h3>
              {alert.actionRecommendation ? (
                renderBullets(
                  alert.actionRecommendation
                    .split('\n')
                    .map(line => line.trim())
                    .filter(Boolean),
                  'actions'
                )
              ) : (
          <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <span>Review recent customer interactions to confirm the opportunity.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 mt-0.5" />
                    <span>Align with internal stakeholders on fit and timing.</span>
                  </li>
          </ul>
              )}
            </section>
          </div>
        )}
      </div>
    </MainPane>
  )
}
